import { useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { TERMINAL_STATES, POLL_INTERVAL_MS } from '../lib/scanStatus';

/** Consecutive fetch failures tolerated (transient network/token blips) before the
 *  poll loop gives up — without this, one error left the interval running forever
 *  (every POLL_INTERVAL_MS, unbounded) since only a terminal STATUS cleared it, never
 *  a rejected fetch. */
const MAX_CONSECUTIVE_ERRORS = 3;

/**
 * Polls a scan-status fetcher every POLL_INTERVAL_MS until the status is terminal,
 * then clears the interval and fires onComplete once. Shared by ScanProgress and
 * DiscoveryScanProgress — the only difference between them is the fetcher and the
 * result-text they build from the returned status.
 *
 * `fetcher` and `onComplete` are held in refs so the polling effect depends only on
 * `[executionArn, token]`. That matches the pre-refactor ScanProgress deps exactly
 * (no re-subscribe when the caller passes a fresh closure each render).
 */
export function usePolledStatus<T extends { status: string }>(
  fetcher: (executionArn: string, token: string | undefined) => Promise<T>,
  executionArn: string,
  onComplete?: () => void,
): { status: T | null; error: string | null } {
  const { token } = useAuth();
  const [status, setStatus] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let consecutiveErrors = 0;

    async function poll() {
      try {
        const result = await fetcherRef.current(executionArn, token ?? undefined);
        if (!cancelled) {
          consecutiveErrors = 0;
          setStatus(result);
          setError(null); // a prior transient error recovered — stop showing it
          if (TERMINAL_STATES.has(result.status) && intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            onCompleteRef.current?.();
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          consecutiveErrors += 1;
          setError(err instanceof Error ? err.message : 'Unknown error');
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS && intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    }

    void poll();
    intervalRef.current = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [executionArn, token]);

  return { status, error };
}
